// ============================================================================
// shared user-directory data owner
// ============================================================================
// This provider owns the full profile directory used by admin user management.
// It intentionally does NOT own the signed-in caller profile: App.loadUser is
// still the only auth-readiness path. The directory is requested on demand,
// never at provider mount or application boot.
//
// The PHYSICAL read moved to src/lib/userDirectoryApi.ts (ask 3: a component
// owns the decision to read, a lib module owns the PostgREST chain). Every
// scoping rule below is unchanged and stays here, because scope, session epoch
// and the superseded-response guard are this provider's job, not the
// transport's.
// ============================================================================

import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';

import {appQueryClient} from '../app/data/queryClient.js';
import {scopeOrNull} from '../app/data/routeDataCatalog.js';
import type {AuthenticatedScope, QueryKey} from '../app/data/routeDataCatalog.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {sb} from '../lib/supabase.js';
// The PostgREST chain itself lives in src/lib/userDirectoryApi.ts. This module
// keeps the DECISION to read: the click that triggers it, the epoch-scoped
// query key, and the superseded-response guard. It was the only importer of
// `readUserDirectory` when the function lived here, so the move needs no
// re-export.
import {readUserDirectory} from '../lib/userDirectoryApi.js';
import {userDirectoryQueryKey, type UserDirectorySessionEpoch} from './userDirectoryRegistry.js';

type DirectorySnapshot = {
  readonly identity: string | null;
  readonly rows: unknown[];
};

// A41: user management is the Admin Users tab (/admin?tab=users), its one
// mount point, so the former modal show/open state is gone.
type UserDirectoryContextValue = {
  readonly allUsers: unknown[];
  readonly setAllUsers: React.Dispatch<React.SetStateAction<unknown[]>>;
  /** Resolves true when the roster loaded, false when it did not (A41 banner). */
  readonly loadUsers: () => Promise<boolean>;
};

const UserDirectoryContext = createContext<UserDirectoryContextValue | null>(null);

function authenticatedDirectoryKey(
  scope: AuthenticatedScope | null,
  sessionEpoch: UserDirectorySessionEpoch,
): QueryKey | null {
  return scope ? userDirectoryQueryKey(scope, sessionEpoch) : null;
}

export function UserDirectoryProvider({children}: {children: ReactNode}) {
  // Session epoch is an AuthContext sibling, not an authState field. Reading
  // it directly is what separates a fresh login by the same subject/role from
  // the prior session's cached directory rows.
  const {authState, rolePreview, sessionEpoch} = useAuth();

  // `scopeOrNull` explicitly treats unresolved auth as no scope. A signed-out
  // public scope is also deliberately excluded: there is no public directory.
  const scope = useMemo(() => {
    const candidate = scopeOrNull(authState, rolePreview);
    return candidate && candidate.kind === 'authenticated' ? candidate : null;
  }, [authState, rolePreview]);
  const queryKey = useMemo(() => authenticatedDirectoryKey(scope, sessionEpoch), [scope, sessionEpoch]);
  const identity = useMemo(() => (queryKey ? JSON.stringify(queryKey) : null), [queryKey]);

  const [snapshot, setSnapshot] = useState<DirectorySnapshot>({identity: null, rows: []});

  // Never expose a previous session's directory for even one render while an
  // auth transition is settling. The Query key already isolates cached rows;
  // this receipt stamps the local mutation projection with the same identity.
  const allUsers = useMemo(() => (snapshot.identity === identity ? snapshot.rows : []), [snapshot, identity]);
  const identityRef = useRef(identity);
  identityRef.current = identity;

  const setAllUsers = useCallback<React.Dispatch<React.SetStateAction<unknown[]>>>((next) => {
    const targetIdentity = identityRef.current;
    setSnapshot((previous) => {
      const previousRows = previous.identity === targetIdentity ? previous.rows : [];
      const rows = typeof next === 'function' ? next(previousRows) : next;
      return {identity: targetIdentity, rows};
    });
  }, []);

  const loadUsers = useCallback(async (): Promise<boolean> => {
    if (!queryKey || !identity) return false;
    const requestIdentity = identity;
    try {
      const rows = await appQueryClient.fetchQuery({
        queryKey,
        // Destructuring signal makes this request cancellable when Query
        // supersedes it; readUserDirectory applies it to the PostgREST chain.
        queryFn: ({signal}) => readUserDirectory(sb, signal),
      });
      if (identityRef.current === requestIdentity) {
        setSnapshot({identity: requestIdentity, rows});
      }
      return true;
    } catch (error) {
      // Preserve the old on-demand contract: a failed list does not replace
      // good same-session rows with a fabricated empty roster, and callers may
      // retry by opening Users again or re-entering the Webforms editor.
      console.error('user directory load error:', error);
      return false;
    }
  }, [identity, queryKey]);

  const value = useMemo<UserDirectoryContextValue>(
    () => ({allUsers, setAllUsers, loadUsers}),
    [allUsers, setAllUsers, loadUsers],
  );

  return <UserDirectoryContext.Provider value={value}>{children}</UserDirectoryContext.Provider>;
}

export function useUserDirectory(): UserDirectoryContextValue {
  const value = useContext(UserDirectoryContext);
  if (!value) throw new Error('useUserDirectory must be used within UserDirectoryProvider');
  return value;
}
